// src/components/Account.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios8001 from '../services/axios8001'; 
import axios8200 from '../services/axios8200'; 
import AccountNavbar from './AccountNavbar';
import './Account.css';

const AccountPage = () => {
  const { id: accountId } = useParams();  
  const navigate = useNavigate();  
  const [accountDetails, setAccountDetails] = useState({});
  const [sections, setSections] = useState({
    "ACCOUNT HEALTH ASSESSMENT": [],
    "VALUE PYRAMID": [],
    "MACRO ECONOMIC & INDUSTRY TRENDS": [],
    "BUSINESS ISSUES & OBJECTIVES": [],
    "RELATIONSHIP MAPPING": [],
    "WHITESPACE MAPPING": [],
    "VALUE CREATION MAPPING": [],
    "ACTIONABLE QTR BY QTR PLAN": [],
    "Next Steps": []
  });
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [updateSection, setUpdateSection] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [industry, setIndustry] = useState('');
  const [solutionTypes, setSolutionTypes] = useState('');
  const [vendorNames, setVendorNames] = useState('');
  const userId = sessionStorage.getItem('user_id');

  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const response = await axios8001.get('/get_account_details', {
          params: { account_id: accountId }
        });

        if (response.data) {
          setCustomerId(response.data.customer_id);
          setCustomerName(response.data.customer_name);
          setIndustry(response.data.industry || ''); 
          setAccountDetails(response.data);
          
          const fetchedSections = response.data.sections || {};
          const updatedSections = {
            ...sections,
            ...fetchedSections
          };
          setSections(updatedSections);
        } else {
          console.error('Account details not found.');
        }
      } catch (error) {
        console.error('Error fetching account details:', error);
      }
    };

    if (accountId) {
      fetchAccountDetails();
    }
  }, [accountId]);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleSendMessage = async () => {
    if (!accountId) {
      alert('Account ID is missing. Unable to proceed.');
      return;
    }

    if (inputValue.trim() && updateSection) {
      setIsSending(true);

      try {
        const payload = {
          user_id: userId,
          api_key: sessionStorage.getItem('api_key'),
          account_id: accountId,
          section: updateSection,
          content: inputValue,
          customer_name: customerName,
        };

        const response = await axios8001.post('/update_account_section', payload);

        setSections(prevSections => ({
          ...prevSections,
          [updateSection]: [...prevSections[updateSection], ...response.data.updated_sections[updateSection]]
        }));
      } catch (error) {
        console.error('Error updating account section:', error);
      } finally {
        setIsSending(false);
        setInputValue('');
      }
    } else {
      alert('Please select a section and provide content to update.');
    }
  };

  const handleSectionSelection = (section) => {
    setUpdateSection(section);
  };

  const handleAnalyze = async () => {
    if (!accountId) {
      alert('Account ID is missing. Unable to proceed.');
      return;
    }

    let section = updateSection;
    if (!section) {
      section = prompt('Which section would you like to analyze?');
      if (!section) return;
      setUpdateSection(section);
    }

    setIsAnalyzing(true);

    try {
      const payload = {
        user_id: userId,
        api_key: sessionStorage.getItem('api_key'),
        account_id: accountId,
        customer_name: customerName,
        section,
      };

      const response = await axios8200.post('/analyseaccount/', payload);
      alert('Analysis completed successfully!');
    } catch (error) {
      console.error('Error analyzing account section:', error);
      alert(`Failed to analyze account section: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleBuildReviewValuePyramid = () => {
    const user_id = sessionStorage.getItem('user_id');
    const api_key = sessionStorage.getItem('api_key');

    if (!user_id || !api_key || !accountId) {
        alert("Missing user_id, api_key, or account_id. Cannot proceed.");
        return;
    }

    // Get or prompt for solution types and vendor names
    const solutionTypesInput = prompt("Enter solution types (comma separated):", solutionTypes);
    const vendorNamesInput = prompt("Enter vendor names (comma separated):", vendorNames);

    // If either is missing, alert and return
    if (!solutionTypesInput || !vendorNamesInput) {
        alert("Solution types and vendor names are required.");
        return;
    }

    // Parse inputs into arrays
    const solutionTypesArray = solutionTypesInput.split(',').map(type => type.trim());
    const vendorNamesArray = vendorNamesInput.split(',').map(vendor => vendor.trim());

    setSolutionTypes(solutionTypesArray);
    setVendorNames(vendorNamesArray);

    // Construct URL with query parameters
    const queryString = `?user_id=${encodeURIComponent(user_id)}&api_key=${encodeURIComponent(api_key)}&account_id=${encodeURIComponent(accountId)}&solution_types=${encodeURIComponent(solutionTypesArray.join(','))}&vendor_names=${encodeURIComponent(vendorNamesArray.join(','))}`;
    
    // Navigate to valuepyramid page with account_id and other necessary parameters
    navigate(`/valuepyramid/${accountId}${queryString}`);
  };

  return (
    <div className="account-page">
      {/* Use the AccountNavbar component instead of Navbar */}
      <AccountNavbar />

      <button className="build-review-button" onClick={handleBuildReviewValuePyramid}>
        Build and Review Value Pyramid
      </button>

      <div className="account-container">
        <h3>Account Details</h3>
        <div className="fields">
          <p>Customer Name: {customerName}</p>
          <p>Account ID: {accountId}</p>
          <p>Customer ID: {customerId}</p>
          <p>Industry: {industry || "Not provided"}</p>
        </div>

        <div className="sections">
          <h3>Sections</h3>
          {Object.keys(sections).map((section, index) => (
            <div key={index}>
              <h4>{section}</h4>
              {sections[section].map((item, idx) => (
                <p key={idx}>
                  <strong>Content:</strong> {item.content} <br />
                  <strong>Timestamp:</strong> {item.timestamp} <br />
                  <strong>Updated by:</strong> {item.updated_by}
                </p>
              ))}
            </div>
          ))}
        </div>

        <div className="interaction-buttons">
          <select onChange={(e) => handleSectionSelection(e.target.value)}>
            <option value="">Select Section</option>
            {Object.keys(sections).map((section, index) => (
              <option key={index} value={section}>{section}</option>
            ))}
          </select>

          <textarea
            className="input-box"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Update section or send a message..."
          />
          <button className="send-button" onClick={handleSendMessage} disabled={isSending}>
            {isSending ? 'Sending...' : 'Send'}
          </button>
        </div>

        <button className="analyze-button" onClick={handleAnalyze} disabled={isAnalyzing}>
          {isAnalyzing ? 'Analyzing...' : 'Analyze'}
        </button>

      </div>
    </div>
  );
};

export default AccountPage;
